import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Play, Sparkles, Crosshair, Megaphone, MessageSquare, Gift,
  Footprints, Map, Check, Navigation as NavIcon, Activity, TrendingUp, Target,
  MapPin, Award, Radio, Smartphone, Store, Coins, Monitor, ThumbsUp, Tv,
  Layout, FileText, Search, Users, Repeat, BarChart3, LineChart, Gauge, Settings,
  Zap, Video, Layers
} from "lucide-react";
import { FEATURED_PACKAGES, CUSTOMER_JOURNEY_URL } from "../data/constants";
import { PackageCard } from "../components/PackageCard";
import { Testimonials } from "../components/Testimonials";
import { SEO } from "../components/SEO";

const HomePage = () => {
  const navigate = useNavigate();

  return (
    <div className="pt-20" data-testid="home-page">
      <SEO
        title="Why FunAsia Digital — Platform Overview"
        description="The FunAsia Digital platform overview — SmartReach™ technology, dual loyalty rewards, foot-traffic focus, and the full customer journey framework that drives real business results."
        path="/overview"
      />

      {/* Hero */}
      <section className="section-padding bg-gradient-to-br from-orange-light via-white to-white" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <span className="inline-flex items-center gap-2 text-orange-primary font-semibold text-sm mb-4 bg-orange-light px-4 py-2 rounded-full">
              <Footprints size={16} />
              Foot Traffic · Real Results · Real Customers
            </span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-gray-dark mb-6 leading-tight">
              The Advertising Platform That{" "}
              <span className="text-gradient-orange">Drives Real Customers</span>{" "}
              to Your Business
            </h1>
            <p className="text-lg md:text-xl text-gray-medium mb-8 max-w-3xl mx-auto">
              FunAsia Digital combines digital advertising, radio, hyperlocal targeting, and loyalty
              rewards to drive in-store visits, online conversions, and measurable business growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => navigate("/contact")} className="btn-primary text-lg flex items-center justify-center gap-2" data-testid="hero-get-customers-btn">
                Get More Customers <ArrowRight size={20} />
              </button>
              <button onClick={() => navigate("/contact")} className="btn-secondary text-lg flex items-center justify-center gap-2" data-testid="hero-book-demo-btn">
                Book a Demo <Play size={18} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Real World Results */}
      <section className="section-padding bg-white" data-testid="real-world-results-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              Not Just Ads. <span className="text-gradient-orange">Real Customers. Real Visits. Real Revenue.</span>
            </h2>
            <p className="text-gray-medium max-w-2xl mx-auto text-lg">We focus on outcomes, not just impressions.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Store, title: "Drive In-Store Visits", desc: "Bring real people through your doors with location-aware campaigns." },
              { icon: Footprints, title: "Increase Foot Traffic", desc: "Activate hyperlocal demand and convert nearby audiences into walk-ins." },
              { icon: Users, title: "Convert Local Customers", desc: "Reach high-intent local shoppers ready to take action right now." },
              { icon: LineChart, title: "Measure Real Outcomes", desc: "Track visits, revenue, and ROI — not just clicks and impressions." }
            ].map((item) => (
              <div key={item.title} className="bg-white p-6 rounded-2xl shadow-card card-hover border border-gray-100" data-testid={`result-card-${item.title.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="w-14 h-14 rounded-xl bg-orange-light flex items-center justify-center mb-4">
                  <item.icon className="text-orange-primary" size={28} />
                </div>
                <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{item.title}</h3>
                <p className="text-gray-medium text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How SmartReach Works */}
      <section className="section-padding bg-gray-light" data-testid="smartreach-how-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 text-orange-primary font-semibold text-sm mb-3 bg-white px-4 py-2 rounded-full shadow-sm">
              <Sparkles size={16} /> FunAsia SmartReach&trade; Technology
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              How FunAsia SmartReach&trade; <span className="text-gradient-orange">Drives Customers to Your Door</span>
            </h2>
            <p className="text-gray-medium max-w-2xl mx-auto">
              A proprietary system combining digital, radio, hyperlocal targeting, and loyalty rewards.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { icon: Crosshair, num: "01", title: "Identify Target Customers", desc: "Hyperlocal targeting using behavioral and location signals." },
              { icon: Megaphone, num: "02", title: "Reach Across Channels", desc: "Radio, digital, mobile, social, and DOOH — all unified." },
              { icon: MessageSquare, num: "03", title: "Engage With Offers", desc: "Time-based, locally relevant promotions and messaging." },
              { icon: Gift, num: "04", title: "Reward With Loyalty Points", desc: "Dual rewards drive engagement and repeat visits." },
              { icon: Footprints, num: "05", title: "Drive In-Store Visits", desc: "Measurable foot traffic and real customer conversions." }
            ].map((step) => (
              <div key={step.num} className="relative bg-white p-6 rounded-2xl shadow-card card-hover border border-gray-100" data-testid={`smartreach-step-${step.num}`}>
                <span className="absolute top-4 right-4 text-3xl font-display font-bold text-orange-primary/20">{step.num}</span>
                <div className="w-12 h-12 rounded-lg gradient-orange flex items-center justify-center mb-4">
                  <step.icon className="text-white" size={24} />
                </div>
                <h3 className="font-display font-bold text-base text-gray-dark mb-2">{step.title}</h3>
                <p className="text-gray-medium text-sm">{step.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <button onClick={() => navigate("/smartreach")} className="btn-primary inline-flex items-center gap-2" data-testid="smartreach-learn-more-btn">
              Explore SmartReach&trade; <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* Hyperlocal Targeting */}
      <section className="section-padding bg-white" data-testid="hyperlocal-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 text-orange-primary font-semibold text-sm mb-4">
                <Map size={16} /> Hyperlocal Reach
              </span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
                Hyperlocal Targeting That <span className="text-gradient-orange">Reaches Customers Nearby</span>
              </h2>
              <p className="text-gray-medium mb-6 text-lg">
                FunAsia Digital uses hyperlocal targeting powered by location intelligence and foot traffic
                insights to reach customers near your business — exactly when it matters.
              </p>
              <ul className="space-y-3">
                {[
                  "Target by location radius around your store",
                  "Reach customers near competitor locations",
                  "Engage high-intent local audiences",
                  "Activate campaigns in real time"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3" data-testid={`hyperlocal-bullet-${idx}`}>
                    <div className="w-6 h-6 rounded-full bg-orange-light flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={14} className="text-orange-primary" />
                    </div>
                    <span className="text-gray-dark">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="aspect-square bg-gradient-to-br from-orange-primary to-orange-secondary rounded-3xl p-8 flex items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                  {[...Array(6)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute border-2 border-white rounded-full"
                      style={{
                        width: `${(i + 1) * 80}px`,
                        height: `${(i + 1) * 80}px`,
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)"
                      }}
                    />
                  ))}
                </div>
                <div className="relative text-center text-white z-10">
                  <NavIcon size={80} className="mx-auto mb-4" strokeWidth={1.5} />
                  <p className="font-display text-2xl font-bold">Location Intelligence</p>
                  <p className="text-white/90 text-sm mt-2">Powered by foot traffic data</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Foot Traffic Analytics */}
      <section className="section-padding bg-gray-light" data-testid="foot-traffic-analytics-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              Understand and <span className="text-gradient-orange">Measure Foot Traffic</span>
            </h2>
            <p className="text-gray-medium max-w-2xl mx-auto text-lg">
              Track how advertising impacts real-world visits to your business.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Activity, title: "Foot Traffic Insights", desc: "See exactly how many people visit after seeing your ads." },
              { icon: TrendingUp, title: "Visit Trends", desc: "Identify seasonality, peaks, and growth patterns over time." },
              { icon: Target, title: "Campaign Impact", desc: "Attribute lift to specific channels, creatives, and offers." },
              { icon: MapPin, title: "Location Performance", desc: "Compare locations and double-down on what works." }
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-6 rounded-2xl shadow-card card-hover" data-testid={`analytics-card-${idx}`}>
                <item.icon className="text-orange-primary mb-4" size={32} />
                <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{item.title}</h3>
                <p className="text-gray-medium text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* Dual Loyalty System */}
      <section className="section-padding bg-white" data-testid="dual-loyalty-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 text-orange-primary font-semibold text-sm mb-3 bg-orange-light px-4 py-2 rounded-full">
              <Award size={16} /> Dual Loyalty Rewards
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              Drive Repeat Customers With <span className="text-gradient-orange">Dual Loyalty Rewards</span>
            </h2>
            <p className="text-gray-medium max-w-2xl mx-auto text-lg">
              Customers earn rewards while engaging with your brand — online and in-store.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              { icon: Radio, title: "Radio Listening Points", desc: "Listeners earn points from radio engagement on the FunAsia network.", color: "from-orange-primary to-orange-secondary" },
              { icon: Smartphone, title: "Digital Engagement Points", desc: "Earned through app interactions, ads, and online activity.", color: "from-orange-secondary to-orange-primary" },
              { icon: Store, title: "In-Store Visit Rewards", desc: "Customers earn points when visiting participating businesses.", color: "from-orange-primary to-orange-secondary" }
            ].map((item, idx) => (
              <div key={idx} className="relative bg-white rounded-2xl p-6 shadow-card card-hover border border-gray-100" data-testid={`loyalty-card-${idx}`}>
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-4`}>
                  <item.icon className="text-white" size={28} />
                </div>
                <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{item.title}</h3>
                <p className="text-gray-medium text-sm">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-br from-gray-dark to-gray-900 rounded-3xl p-8 md:p-12 text-white">
            <h3 className="font-display text-2xl font-bold text-center mb-8">The Points Ecosystem</h3>
            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
              {[
                { icon: Radio, label: "Radio" },
                { icon: ArrowRight },
                { icon: Smartphone, label: "Digital" },
                { icon: ArrowRight },
                { icon: Store, label: "Store Visit" },
                { icon: ArrowRight },
                { icon: Coins, label: "Rewards" }
              ].map((item, idx) =>
                item.label ? (
                  <div key={idx} className="flex flex-col items-center gap-2 bg-white/10 backdrop-blur-sm rounded-2xl p-5 min-w-[110px]">
                    <item.icon className="text-orange-secondary" size={32} />
                    <span className="font-medium text-sm">{item.label}</span>
                  </div>
                ) : (
                  <item.icon key={idx} className="text-orange-secondary" size={20} />
                )
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
              {["Increases repeat visits", "Drives higher engagement", "Builds long-term loyalty", "Encourages real-world actions"].map((b, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-white/5 rounded-lg p-3">
                  <Check size={16} className="text-orange-secondary flex-shrink-0" />
                  <span className="text-sm">{b}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Omnichannel */}
      <section className="section-padding bg-gray-light" data-testid="omnichannel-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              One Platform. Every Channel. <span className="text-gradient-orange">Maximum Impact.</span>
            </h2>
            <p className="text-gray-medium max-w-2xl mx-auto text-lg">
              We combine traditional and digital channels to maximize reach and conversion.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Radio, label: "Radio", sub: "FunAsia network" },
              { icon: Monitor, label: "Digital Display" },
              { icon: Smartphone, label: "Mobile Ads" },
              { icon: ThumbsUp, label: "Social Media" },
              { icon: Tv, label: "Connected TV" },
              { icon: Layout, label: "DOOH" },
              { icon: FileText, label: "Print Media" },
              { icon: Search, label: "Search Marketing" }
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-6 rounded-2xl shadow-card card-hover text-center" data-testid={`channel-${idx}`}>
                <item.icon className="text-orange-primary mx-auto mb-3" size={36} />
                <h4 className="font-display font-semibold text-gray-dark">{item.label}</h4>
                {item.sub && <p className="text-xs text-gray-medium mt-1">{item.sub}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why FunAsia */}
      <section className="section-padding bg-white" data-testid="why-funasia-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              Why Businesses Choose <span className="text-gradient-orange">FunAsia Digital</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Crosshair, title: "Hyperlocal Targeting", desc: "Reach customers within precise radii of your locations." },
              { icon: Footprints, title: "Foot Traffic Focus", desc: "We optimize for real visits, not vanity metrics." },
              { icon: Sparkles, title: "SmartReach\u2122 Technology", desc: "Proprietary system combining radio, digital & loyalty." },
              { icon: Award, title: "Dual Loyalty Rewards", desc: "Earn-and-redeem ecosystem that drives repeat visits." },
              { icon: Megaphone, title: "Omnichannel Campaigns", desc: "One platform for every channel your customers use." },
              { icon: Users, title: "Strong Local Audience", desc: "Built-in reach across the FunAsia community." }
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-6 rounded-2xl shadow-card card-hover border border-gray-100" data-testid={`why-card-${idx}`}>
                <div className="w-12 h-12 rounded-xl bg-orange-light flex items-center justify-center mb-4">
                  <item.icon className="text-orange-primary" size={24} />
                </div>
                <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{item.title}</h3>
                <p className="text-gray-medium text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Packages */}
      <section id="featured" className="section-padding bg-gray-light" data-testid="featured-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">Featured Packages</h2>
            <p className="text-gray-medium max-w-2xl mx-auto">
              Choose the perfect digital growth solution for your business
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURED_PACKAGES.map((pkg) => (
              <PackageCard key={pkg.id} pkg={pkg} />
            ))}
          </div>
        </div>
      </section>

      {/* DSP Tile */}
      <section className="section-padding" data-testid="dsp-promo-section">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-br from-gray-dark to-gray-900 rounded-3xl p-8 md:p-12 text-white">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <span className="inline-flex items-center gap-2 text-orange-secondary font-semibold text-sm mb-4">
                  <Zap size={16} /> NEW SERVICE
                </span>
                <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">Programmatic DSP</h2>
                <p className="text-gray-300 text-lg mb-6">
                  Self-Serve Paid Media — Launch programmatic campaigns with transparent reporting and full control.
                  Access premium inventory across display, video, native, and mobile.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <button onClick={() => navigate("/dsp")} className="btn-primary" data-testid="home-explore-dsp-btn">
                    Explore DSP <ArrowRight size={18} className="inline ml-2" />
                  </button>
                  <button onClick={() => navigate("/dsp/get-access")} className="btn-secondary border-white text-white hover:bg-white/10">
                    Get Access
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Monitor, label: "Display Ads" },
                  { icon: Video, label: "Video Ads" },
                  { icon: Layout, label: "Native Ads" },
                  { icon: Smartphone, label: "Mobile/In-App" }
                ].map((item, idx) => (
                  <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                    <item.icon className="mx-auto mb-2 text-orange-secondary" size={32} />
                    <p className="text-sm font-medium">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Journey */}
      <section className="section-padding bg-gray-light" data-testid="customer-journey-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">Our Customer Journey Framework</h2>
            <p className="text-gray-medium max-w-2xl mx-auto">A full-funnel system to attract, convert, and retain customers.</p>
          </div>

          <div className="flex justify-center mb-12">
            <img src={CUSTOMER_JOURNEY_URL} alt="Customer Journey Framework" className="max-w-full md:max-w-3xl rounded-2xl shadow-card-hover" data-testid="customer-journey-image" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Target, title: "Attract", desc: "Ads, Listings, Reviews, SEO, DSP", color: "text-blue" },
              { icon: MessageSquare, title: "Convert", desc: "Conversations AI + Lead Capture", color: "text-orange-primary" },
              { icon: Repeat, title: "Engage", desc: "Campaigns, Social, Review Requests", color: "text-teal" },
              { icon: BarChart3, title: "Core", desc: "CRM, Reporting, Automations", color: "text-purple" }
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-card text-center card-hover">
                <item.icon className={`${item.color} mx-auto mb-4`} size={40} />
                <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{item.title}</h3>
                <p className="text-gray-medium text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="section-padding" data-testid="trust-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Users, label: "100+ Clients", desc: "Trusted by businesses" },
              { icon: BarChart3, label: "Transparent Reporting", desc: "Real-time insights" },
              { icon: Target, label: "ROI Focused", desc: "Results that matter" },
              { icon: Layers, label: "White-Label Ready", desc: "Partner with us" }
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-card text-center">
                <item.icon className="text-orange-primary mx-auto mb-3" size={32} />
                <h4 className="font-display font-bold text-gray-dark">{item.label}</h4>
                <p className="text-gray-medium text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results Focus */}
      <section className="section-padding" data-testid="results-focus-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              Built to Deliver <span className="text-gradient-orange">Measurable Results</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Gauge, label: "Track performance", desc: "Real dashboards, real numbers." },
              { icon: Settings, label: "Optimize campaigns", desc: "Continuous improvement loops." },
              { icon: TrendingUp, label: "Improve ROI", desc: "Spend less, earn more." },
              { icon: Repeat, label: "Increase repeat customers", desc: "Loyalty that compounds." }
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-6 rounded-2xl shadow-card text-center card-hover" data-testid={`results-card-${idx}`}>
                <item.icon className="text-orange-primary mx-auto mb-3" size={36} />
                <h4 className="font-display font-bold text-gray-dark mb-1">{item.label}</h4>
                <p className="text-gray-medium text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding gradient-orange" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to drive real customers to your door?
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Let SmartReach&trade; turn local audiences into loyal, repeat customers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigate("/contact")} className="bg-white text-orange-primary px-8 py-4 rounded-full font-semibold text-lg hover:shadow-lg transition-all duration-300 hover:-translate-y-1" data-testid="cta-get-customers-btn">
              Get More Customers <ArrowRight size={20} className="inline ml-2" />
            </button>
            <button onClick={() => navigate("/store")} className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all duration-300" data-testid="cta-shop-btn">
              Shop Services
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
